import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Form, notification, Modal, Button, Skeleton } from 'antd';
import { isEmpty } from 'lodash';

import moment from 'moment';

import { PageProps } from '@/types/PageProps';

import TabContentWrapper from '@/components/_common/TabContentWrapper';

import {
  InventoryStatus,
  UpdateInventoryRequest,
} from '@/core/domain/inventory/inventories/Inventory.types';

import { SaveButton } from '@/components/_common';
import InventoryDetails from '@/components/Movements/Inventory/InventoryDetails';
import { queryClient } from '@/Root';
import { InventoryFormValues } from '@/core/domain/inventory/inventories/Inventory.types';
import {
  useInventory,
  useRemoveInventory,
  useUpdateInventory,
} from '@/core/domain/inventory/inventories/inventory.hooks';
import Authorities from '@/types/Authority';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { hasPermissions } from '@/utils/functions/hasPermissions';

function InventoryDetailsPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<InventoryFormValues>();
  const history = useHistory();

  const { inventoryId } = useRouteMatch<{ inventoryId: string }>().params;

  const { data: inventoryData, isLoading } = useInventory(+inventoryId);

  const updateMutation = useUpdateInventory();
  const removeMutation = useRemoveInventory();

  useEffect(() => {
    if (inventoryData && !isEmpty(inventoryData)) {
      form?.setFieldsValue({
        ...inventoryData,
        date: moment(inventoryData.date),
      });
    }
  }, [inventoryData]);

  const handleSaveClick = async () => {
    try {
      const { ...values } = await form.validateFields();

      const obj: UpdateInventoryRequest = {
        id: Number(inventoryId),
        date: values.date.format('YYYY-MM-DD'),
        reason: values.reason,
      };

      await updateMutation.mutateAsync(obj);

      await queryClient.refetchQueries(['inventory', inventoryId]);
    } catch (err) {
      notification.error({
        message: intl.formatMessage({
          id: 'commons.form.notification.error',
        }),
        description: intl.formatMessage({
          id: 'commons.form.notification.errorDescription',
        }),
      });
    }
  };

  const handleRemoveClick = () => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'inventory.notifications.remove.confirm.title',
      }),
      content: `${intl.formatMessage({
        id: 'inventory.notifications.remove.confirm.content',
      })}`,
      onOk: async () => {
        await removeMutation.mutateAsync(Number(inventoryId));

        history.push('/app/movements/inventory');
      },
      okType: 'danger',
    });
  };

  return (
    <TabContentWrapper
      title={route.name}
      extra={
        <SpaceForButton>
          <Button
            block
            danger
            onClick={handleRemoveClick}
            loading={removeMutation.isLoading}
            disabled={
              !hasPermissions([Authorities.MOVIMENTATION_INVENTORY_DELETE]) ||
              inventoryData?.status === InventoryStatus.DONE ||
              inventoryData?.status === InventoryStatus.PROCESSING
            }
          >
            {intl.formatMessage({ id: 'commons.entity.remove' })}
          </Button>

          <SaveButton
            block
            type="primary"
            onClick={handleSaveClick}
            loading={updateMutation.isLoading}
            disabled={
              !hasPermissions([Authorities.MOVIMENTATION_INVENTORY_EDIT])
            }
          >
            {intl.formatMessage({ id: 'commons.form.button.save' })}
          </SaveButton>
        </SpaceForButton>
      }
    >
      <Skeleton loading={isLoading}>
        {inventoryData && (
          <InventoryDetails inventory={inventoryData} form={form} />
        )}
      </Skeleton>
    </TabContentWrapper>
  );
}

export default InventoryDetailsPage;
